import React, { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components";
import { userManagementApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { UsersTable, type UserItem } from "@/pages/Users/UsersTable";
import { NewUserModal } from "@/pages/Users/NewUserModal";
import { Icon } from "@/components/Icon";

export const Users: React.FC = () => {
    const { t } = useTranslation("global");
    const { isAdmin } = useAuth();
    const { showError } = useToast();
    const [items, setItems] = useState<UserItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useStoredPreference(
        "vigepra_users_search",
        z.string().max(500),
        "",
        true
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false);
    const requestInFlight = useRef(false);

    const activeCompanyId = localStorage.getItem("vigepra_active_company");
    const isMainCompany = !activeCompanyId || activeCompanyId === "main";

    const fetchItems = async (): Promise<void> => {
        if (requestInFlight.current) {
            return;
        }

        requestInFlight.current = true;
        setLoading(true);
        setErrorMessage(null);
        try {
            const res = await userManagementApi.getAll();
            if (res.result) {
                const users = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.users)
                        ? res.data.users
                        : null;

                if (users) {
                    setItems(users);
                } else {
                    throw new Error(t("common.error"));
                }
            } else {
                throw new Error(res.message || t("common.error"));
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t("common.error");
            setErrorMessage(msg);
            showError(msg);
            setItems([]);
        } finally {
            requestInFlight.current = false;
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchItems();
    }, []);

    const handleUpdateItem = (updated: UserItem): void => {
        setItems((prev) =>
            prev.map((item) =>
                item.id === updated.id ? { ...item, ...updated } : item
            )
        );
    };

    const handleDeleteItem = (deletedId: number | string): void => {
        setItems((prev) => prev.filter((item) => item.id !== deletedId));
    };

    const displayedItems = items.filter((item) => {
        if (search) {
            const s = search.toLowerCase();
            const name = item.name?.toLowerCase() || "";
            const email = item.email?.toLowerCase() || "";
            const rolesStr =
                !isMainCompany && item.roles
                    ? item.roles
                          .filter((role) =>
                              String(role.tenant_id) === String(activeCompanyId)
                          )
                          .map((r) => r.name.toLowerCase())
                          .join(" ")
                    : "";
            const companiesStr =
                isMainCompany && item.companies
                    ? item.companies.map((c) => c.name.toLowerCase()).join(" ")
                    : "";

            if (
                !name.includes(s) &&
                !email.includes(s) &&
                !rolesStr.includes(s) &&
                !companiesStr.includes(s)
            ) {
                return false;
            }
        }
        return true;
    });

    return (
        <div className="page">
            <PageHeader
                compact
                className="!mb-0"
                title={<span>{t("users.title")}</span>}
                action={
                    isAdmin
                        ? {
                              label: t("users.new_button"),
                              icon: "plusCircle",
                              permissionKey: "users.create",
                              onClick: () => setIsNewModalOpen(true),
                          }
                        : null
                }
            />

            {loading ? (
                <div className="flex min-h-56 items-center justify-center rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 shadow-xs dark:border-white/10 dark:bg-dark-box dark:text-zinc-300">
                    <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 shadow-sm dark:border-white/10">
                        <Icon value="loader" size={16} className="animate-spin text-primary" />
                        <div>{t("table.loading")}</div>
                    </div>
                </div>
            ) : (
                <UsersTable
                    items={displayedItems}
                    loading={false}
                    error={errorMessage}
                    search={search}
                    onSearchChange={setSearch}
                    onUpdateItem={handleUpdateItem}
                    onDeleteItem={handleDeleteItem}
                    onRefresh={fetchItems}
                />
            )}

            <NewUserModal
                isOpen={isNewModalOpen}
                onClose={() => setIsNewModalOpen(false)}
                onSuccess={() => fetchItems()}
            />
        </div>
    );
};
